<!-- =========================================================================================
  File Name: UserCreate.vue
  Description: User Create Page
  ----------------------------------------------------------------------------------------
  Item Name: Vuexy - Vuejs, HTML & Laravel Admin Dashboard Template
  Author: Pixinvent
  Author URL: http://www.themeforest.net/user/pixinvent
========================================================================================== -->

<template>
  <div id="page-company-create" v-if="ready">
    <div class="vx-row">
      <div class="vx-col w-full">
        <vue-element-loading :active="busy"></vue-element-loading>
        <form-wizard
          :title="null"
          :subtitle="null"
          nextButtonText="Continue"
          finish-button-text="Create Entity"
          @on-change="onChange"
          @on-complete="saveChanges"
          color="#000000"
        >
          <tab-content title="General Details" :before-change="validateStepOne">
            <company-general-details
              ref="general"
              class="mt-4"
              v-model="general_data"
            />
            <company-profile
              ref="profile"
              v-model="profile_data"
              :has-lloyds="hasLloyds"
              :lloyds-syndicate="isLloydsSyndicate"
            />
            <company-branding
              ref="branding"
              v-model="branding_data"
            />
            <company-external-data
              ref="external"
              class="mt-4"
              v-model="external_data"
            />
          </tab-content>
          <tab-content title="Addresses" :before-change="validateStepTwo">
            <company-addresses ref="addresses" v-model="addresses_data" />
          </tab-content>
          <tab-content title="TOBAs" :before-change="validateStepThree">
            <company-tobas ref="tobas" class="mt-4" v-model="tobas_data" />
          </tab-content>

          <template slot="footer" slot-scope="props">
            <div class="wizard-footer-left">
              <vs-button
                v-if="props.activeTabIndex > 0"
                class="my-4"
                color="primary"
                @click.native="props.prevTab()"
                >Back</vs-button
              >
              <vs-button class="ml-4 my-4" color="primary" @click="cancel"
                >Cancel</vs-button>
            </div>
            <div class="wizard-footer-right">
              <vs-button
                v-if="!props.isLastStep"
                class="my-4"
                color="light"
                @click.native="props.nextTab()"
                >Continue</vs-button
              >
              <vs-button
                v-if="props.isLastStep"
                class="my-4"
                color="light"
                @click.native="props.nextTab()"
                >Create Entity</vs-button
              >
            </div>
          </template>
        </form-wizard>
      </div>
    </div>
  </div>
</template>

<script>
// Store Module
import { FormWizard, TabContent } from 'vue-form-wizard';
import 'vue-form-wizard/dist/vue-form-wizard.min.css';
import CompanyGeneralDetails from '../forms/CompanyGeneralDetails';
import CompanyProfile from '../forms/CompanyProfile';
import CompanyBranding from '../forms/CompanyBranding';
import FormValidateMixin from '../../../../components/mixins/FormValidateMixin';
import CompanyExternalData from '../forms/CompanyExternalData';
import CompanyAddresses from '../forms/CompanyAddresses';
import CompanyTobas from '../forms/CompanyTobas';
import CompanyValidation from '@/validation/Company';
import NotificationMixin from '../../../../components/mixins/NotificationMixin';
import CanAccessMixin from '@/components/mixins/CanAccessMixin';
import FormModelMixin from '@/components/mixins/FormModelMixin';
import SaveModelMixin from '../../../../components/mixins/SaveModelMixin';
CompanyValidation.init();

export default {
  name: 'CompanyCreate',
  components: {
    CompanyExternalData,
    CompanyGeneralDetails,
    CompanyProfile,
    CompanyAddresses,
    CompanyTobas,
    FormWizard,
    TabContent,
    CompanyBranding,
  },
  mixins: [
    FormModelMixin,
    NotificationMixin,
    CanAccessMixin,
    SaveModelMixin,
    FormValidateMixin
  ],
  data() {
    return {
      data: null,
      ready: false,
      profile_data: {},
      general_data: {},
      branding_data: {},
      user_data: {},
      external_data: {
        webservice_parameters: [],
        webServices: [],
        external_links: []
      },

      formReferences: [],
      additiveForms: [],

      addresses_data: {},
      tobas_data: {},
      forms: ['general', 'profile', 'external', 'addresses', 'tobas']
    };
  },
  computed: {
    hasLloyds() {
      return !!(this.general_data.CompanyType_LloydsStatus_FK && this.general_data.CompanyType_LloydsStatus_FK.id)
    },
    isLloydsSyndicate() {
      let id = parseInt(this.general_data.CompanyType_LloydsStatus_FK);

      if (!id
        && this.general_data.CompanyType_LloydsStatus_FK
        && this.general_data.CompanyType_LloydsStatus_FK.id
      ) {
        id = parseInt(this.general_data.CompanyType_LloydsStatus_FK.id);
      }

      // ID for Lloyds Syndicate
      return (id === 17) ? true : false;
    }
  },
  methods: {
    async validateStepOne() {
      try {
        this.internalLoading = true;
        this.formReferences = ['general', 'profile', 'external'];
        const errors = [];

        if (this.general_data.CompanyCode) {
          const { data } = await this.$api.companies.companyCodeExists(this.general_data.CompanyCode);

          if (data.data) {
            errors.push('Duplicate entity code');
          }
        }

        if (!this.validate()) {
          errors.push('Not all required fields have been provided');
        }

        if (errors.length) {
          errors.forEach(error => this.notifyError(error))

          return false;
        }
      } catch (e) {
        console.log(e);
        this.addErrors(e);
        return false;
      } finally {
        this.internalLoading = false;
      }

      return true;
    },
    async validateStepTwo() {
      try {
        this.internalLoading = true;
        this.formReferences = ['addresses'];

        if (!(await this.validate())) {
          this.notifyError('Not all required fields have been provided');

          return false;
        }
      } catch (e) {
        this.addErrors(e);
        console.log(e);
        return false;
      } finally {
        this.internalLoading = false;
      }

      return true;
    },
    async validateStepThree() {
      try {
        this.internalLoading = true;
        this.formReferences = ['tobas'];

        if (!(await this.validate())) {
          this.notifyError('Not all required fields have been provided');

          return false;
        }
      } catch (e) {
        this.addErrors(e);
        return false;
      } finally {
        this.internalLoading = false;
      }

      return true;
    },
    onChange() {
      this.scrollToTop();
      this.saveLocaldata();
    },
    scrollToTop() {
      window.scrollTo(0, 0);
    },
    saveLocaldata(step) {
      const companyData = {
        ...this.general_data
      };

      companyData.profile = this.profile_data;
      companyData.external_links = this.external_data.external_links;
      companyData.webservice_parameters = this.external_data.webservice_parameters;
      companyData.addresses = this.addresses_data;
      companyData.tobas = this.tobas;
    },
    getParams(validationOnly = false) {
      const companyData = this.setModelCopy({
        ...this.general_data
      });
      companyData.profile = this.profile_data;
      companyData.external_links = this.external_data.external_links;
      companyData.webservice_parameters = this.external_data.webServices;

      // Exclude from validation for step one
      if (!validationOnly) {
        companyData.addresses = this.addresses_data;
        companyData.addresses.IsPrimaryAddress = 1;
        companyData.tobas = this.tobas_data.tobas;
      }

      const references = [
        'CompanyAncestor_FK',
        'CompanyType_Entity_FK',
        'CompanyGroup_FK',
        'CompanyType_LloydsStatus_FK',
        'ParentCompany_FK',
        'ProducerStatusType_FK',
        'SalesCountryType_FK',
        'Contact_UnderwriterExecutive_FK',
        'Contact_AreaManager_FK',
        'Contact_PlacingBroker_FK',
      ];

      const multiReferences = [
        {
          array: 'external_links',
          key: 'LinkType_FK'
        },
        {
          array: 'addresses',
          key: 'AddressType_FK',
          children: [
            {
              array: 'phone_numbers',
              key: ['PhoneType_FK', 'CountryCode']
            }
          ]
        },
        {
          array: 'email_addresses',
          key: ['EmailType_FK']
        },
        {
          array: 'tobas',
          key: 'TOBAType_FK',
          dates: ['StartDate', 'ExpiryDate']
        }
      ];

      this.setMultiReferences(multiReferences, companyData);
      this.setReferences(references, companyData);

      // Is RefData but endpoint requires a string
      if (companyData.profile.PartnershipStatus && companyData.profile.PartnershipStatus.id) {
        companyData.profile.PartnershipStatus = String(companyData.profile.PartnershipStatus.id);
      }
      
      if (companyData.profile.PartnershipSeniority && companyData.profile.PartnershipSeniority.id) {
        companyData.profile.PartnershipSeniority = String(companyData.profile.PartnershipSeniority.id);
      }

      if (companyData.profile.ClassificationDetail && companyData.profile.ClassificationDetail.id) {
        companyData.profile.ClassificationDetail = String(companyData.profile.ClassificationDetail.id);
      }

      if (companyData.profile.Contact_UnderwriterExecutive_FK && companyData.profile.Contact_UnderwriterExecutive_FK.id) {
        companyData.profile.Contact_UnderwriterExecutive_FK = String(companyData.profile.Contact_UnderwriterExecutive_FK.id);
      }

      if (companyData.profile.Contact_AreaManager_FK && companyData.profile.Contact_AreaManager_FK.id) {
        companyData.profile.Contact_AreaManager_FK = String(companyData.profile.Contact_AreaManager_FK.id);
      }

      if (companyData.profile.Contact_PlacingBroker_FK && companyData.profile.Contact_PlacingBroker_FK.id) {
        companyData.profile.Contact_PlacingBroker_FK = String(companyData.profile.Contact_PlacingBroker_FK.id);
      }

      return companyData;
    },
    getLogoParams() {
      const params = new FormData();
      params.set('_method', 'post');
      
      for (const field in this.branding_data) {
        params.set(field, this.branding_data[field]);
      }

      return params;
    },
    async saveChanges() {
      try {
        this.internalLoading = true;

        const params = this.getParams();
        const company = await this.$api.companies.create(params);

        if (this.branding_data.logoFile && company.data.data.Company_PK) {
          const logoData = this.getLogoParams();
          await this.$api.companies.updateLogo(company.data.data.Company_PK, logoData);
        }

        this.$vs.notify({
          color: 'success',
          title: 'Entity created',
          text: 'The entity was successfully created',
          position: 'top-right'
        });

        if (this.contactId) {
          await this.$api.companies.assignUsers(company.data.data.Company_PK, [
            this.contactId
          ]);
        }

        this.$router.push({ name: 'company.list' }).catch(() => {});
      } catch (e) {
        console.log(e);

        this.addErrors(e);

        this.$vs.notify({
          color: 'danger',
          title: 'Error occurred',
          text: 'Not able to create entity. Please try again.',
          iconPack: 'feather',
          icon: 'icon-alert-circle',
          position: 'top-right'
        });
      } finally {
        this.internalLoading = false;
      }
    },
    cancel() {
      this.$router.push({ name: 'company.list' });
    },
    async fetchUser(contactId) {
      try {
        const { data } = await this.$api.users.get(contactId);
        this.user_data = data.data;
      } catch (e) {
        console.error(e);
      } finally {
        if (!this.user_data) {
          this.user_not_found = true;
        }
      }
    },
    setUserData() {
      this.general_data.CompanyName = this.user_data.Name;

      if (this.user_data.email_addresses.length) {
        this.general_data.email_addresses = this.user_data.email_addresses.map(
          value => {
            return {
              id: value.ContactEmailAddress_PK,
              dropdownValue: value.EmailType_FK,
              value: value.EmailAddress,
              primary: parseInt(value.IsPrimaryEmail, 10)
            };
          }
        );
      }

      if (this.user_data.addresses.length) {
        this.general_data.email_addresses = this.user_data.email_addresses;
      }

      if (this.user_data.addresses.length) {
        this.addresses_data = this.user_data.addresses.map(address => {
          if (
            this.user_data.phone_numbers &&
            parseInt(address.IsPrimaryAddress)
          ) {
            address.phone_numbers = this.user_data.phone_numbers;
          } else {
            address.phone_numbers = [];
          }

          return address;
        });
      }

      if (this.user_data.external_links) {
        this.external_data.external_links = this.user_data.external_links;
      }
    },
    async setData() {
      this.general_data = {
        CompanyName: null,
        CompanyCode: null,
        CompanyGroup_FK: null,
        CompanyType_Entity_FK: null,
        ParentCompany_FK: null,
        CompanyAncestor_FK: null,
        ProducerStatusType_FK: null,
        CompanyExtension: null,
        CompanyType_LloydsStatus_FK: null,
        email_addresses: null,
        IsEnabled: null
      };

      if (this.$route.params.contactId) {
        this.contactId = this.$route.params.contactId;

        await this.fetchUser(this.contactId);

        if (this.user_data && this.user_data.id) {
          await this.setUserData();
        }
      }
    }
  },
  async created() {
    this.ready = false;

    // Register Module UserManagement Module
    await this.setData();

    this.ready = true;
  }
};
</script>

<style lang="scss">
.wizard-navigation {
  min-height: 868px;
}
</style>
